import React, { useCallback, useMemo, useState } from 'react';
import { isInitialQueryLoading } from '../../../lib/query/listQuery';
import { Button, Switch, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import DataTable from '../../../components/DataTable';
import { ListPage, buildCogActionsColumn } from '../../../components/list';
import OfferingCategoryFilterBar, { type OfferingCategoryFilterValues } from './OfferingCategoryFilterBar';
import OfferingCategoryViewDrawer from './OfferingCategoryViewDrawer';
import OfferingCategoryFormDrawer from './OfferingCategoryFormDrawer';
import Can from '../../permissions/Can';
import { updateOfferingCategory } from '../api/offeringCategories';
import { useOfferingCategories } from '../hooks/useOfferingCategories';
import { useOfferingCategory } from '../hooks/useOfferingCategory';
import { useCreateOfferingCategory } from '../hooks/useCreateOfferingCategory';
import { useUpdateOfferingCategory } from '../hooks/useUpdateOfferingCategory';
import { useDeleteOfferingCategory } from '../hooks/useDeleteOfferingCategory';
import type { OfferingCategoryListItem, OfferingCategoryFormValues } from '../types';

const defaultFilter: OfferingCategoryFilterValues = {
  search: undefined,
};

const OfferingCategoriesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filter, setFilter] = useState<OfferingCategoryFilterValues>(defaultFilter);
  const [selectedOfferingCategoryId, setSelectedOfferingCategoryId] = useState<string | null>(null);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [formOfferingCategoryId, setFormOfferingCategoryId] = useState<string | null>(null);

  const listParams = useMemo(
    () => ({
      pageNumber: page,
      pageSize,
      search: filter.search || undefined,
    }),
    [
      page,
      pageSize,
      filter.search,
    ]
  );

  const { data: paged, isLoading } = useOfferingCategories(listParams);
  const loadingList = isInitialQueryLoading(isLoading, paged);
  const { data: offeringCategoryDetail, isLoading: loadingOfferingCategory } = useOfferingCategory(
    formMode === 'edit' ? formOfferingCategoryId : null
  );
  const viewOfferingCategory = useOfferingCategory(selectedOfferingCategoryId);
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteOfferingCategory();

  const toggleMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: OfferingCategoryFormValues }) =>
      updateOfferingCategory(id, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['offering-categories'] });
    },
  });

  const createMutation = useCreateOfferingCategory(() => {
    setFormDrawerOpen(false);
  });
  const updateMutation = useUpdateOfferingCategory(formOfferingCategoryId, () => {
    setFormDrawerOpen(false);
  });

  const handleAddOfferingCategory = useCallback(() => {
    setFormMode('add');
    setFormOfferingCategoryId(null);
    setFormDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((id: string) => {
    setFormMode('edit');
    setFormOfferingCategoryId(id);
    setFormDrawerOpen(true);
  }, []);

  const columns: ColumnsType<OfferingCategoryListItem> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'title',
        key: 'title',
        render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
        render: (v: string | null) =>
          v ?? <Typography.Text type="secondary">—</Typography.Text>,
      },
      {
        title: 'Active',
        key: 'status',
        width: 100,
        render: (_: unknown, record: OfferingCategoryListItem) => (
          <Switch
            checked={record.isActive}
            loading={
              toggleMutation.isPending
              && toggleMutation.variables?.id === record.id
            }
            onChange={(checked) => {
              toggleMutation.mutate({
                id: record.id,
                values: {
                  title: record.title,
                  description: record.description ?? undefined,
                  isActive: checked,
                },
              });
            }}
          />
        ),
      },
      buildCogActionsColumn<OfferingCategoryListItem>({
        onView: (record) => setSelectedOfferingCategoryId(record.id),
        onEdit: (record) => handleEdit(record.id),
        onDelete: (record) => deleteMutation.mutate(record.id),
        deleteConfirm: () => ({
          title: 'Delete category',
          content: 'Delete this category? Offerings that use it may need to be updated.',
        }),
        rowNumber: (_record, index) => (page - 1) * pageSize + index + 1,
      }),
    ],
    [deleteMutation, toggleMutation, handleEdit, page, pageSize],
  );

  const handleEditFromView = () => {
    if (selectedOfferingCategoryId) {
      handleEdit(selectedOfferingCategoryId);
    }
  };

  const handleSaveForm = (values: OfferingCategoryFormValues) => {
    if (formMode === 'add') {
      createMutation.mutate(values);
    } else if (formOfferingCategoryId) {
      updateMutation.mutate(values);
    }
  };

  const handlePaginationChange = useCallback((newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  }, []);

  return (
    <>
      <ListPage
        title="Categories"
        subtitle="Organize offerings into categories for catalog navigation and filters."
        actions={(
          <Can permission="offering-categories.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddOfferingCategory}>
              Add category
            </Button>
          </Can>
        )}
        filters={(
          <OfferingCategoryFilterBar
            value={filter}
            onChange={(next) => {
              setPage(1);
              setFilter(next);
            }}
            onReset={() => {
              setPage(1);
              setFilter(defaultFilter);
            }}
          />
        )}
      >
        <DataTable<OfferingCategoryListItem>
          columns={columns}
          dataSource={paged?.items ?? []}
          loading={loadingList}
          rowKey="id"
          bordered
          striped
          pagination={
            paged
              ? {
                  current: paged.pageNumber,
                  pageSize: paged.pageSize,
                  total: paged.totalCount,
                  onChange: handlePaginationChange,
                }
              : undefined
          }
          emptyText="No categories yet. Add one to classify offerings."
        />
      </ListPage>

      <OfferingCategoryViewDrawer
        open={selectedOfferingCategoryId !== null}
        onClose={() => setSelectedOfferingCategoryId(null)}
        offeringCategory={viewOfferingCategory.data}
        loading={viewOfferingCategory.isLoading}
        onEdit={handleEditFromView}
      />

      <OfferingCategoryFormDrawer
        open={formDrawerOpen}
        onClose={() => setFormDrawerOpen(false)}
        mode={formMode}
        offeringCategoryId={formOfferingCategoryId}
        offeringCategory={offeringCategoryDetail}
        loadingOfferingCategory={formMode === 'edit' && formOfferingCategoryId ? loadingOfferingCategory : false}
        onSave={handleSaveForm}
        saving={createMutation.isPending || updateMutation.isPending}
      />
    </>
  );
};

export default OfferingCategoriesPage;
